import { useState, useEffect, useCallback } from 'react';
import { TextField, Button, Box, Stack, Link } from '@mui/material';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { getIfRequiresPin } from '@/repository/OrganizationDAO';
import { addUser } from '@/repository/UserDAO';
import EnterPinModal from './modals/EnterPinModal';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import Loading from '@/components/loading/Loading';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { updateProfileSettings } from '@/repository/UserDAO';
import { getConfigurationByOrgCodeAndId } from '@/repository/SpecificationDAO';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import COLORS from '@/resources/style/Themes';
import { useLoading } from '@/context_providers/loading_context/LoadingContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { Trans } from 'react-i18next';
import LoadingData from '@/components/loading/LoadingData';
import BaseModal from '@/components/modals/BaseModal';
import {
  JoinRequestCreatedResponse,
  MergedUserSelfUpdateSettings,
} from '@/../generated-client/models';

// Passing 'Default' because the backend currently requires group_name
// on OrgUserSelfCreateRequest. Remove this constant once P8 is rolled
const DEFAULT_GROUP_NAME = 'Default';

export const SignUp = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const { user, getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [showPinModal, setShowPinModal] = useState(false);
  const [bearerToken, setBearerToken] = useState('');
  const { doIExist } = useUserContext();
  const { showErrorToast } = useToast();
  const { setPageLoading } = useLoading();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    orgCode: false,
  });
  const [loadingAPIData, setLoadingAPIData] = useState(false);
  const [pendingJoinRequest, setPendingJoinRequest] =
    useState<JoinRequestCreatedResponse | null>(null);
  const errors = {
    firstName:
      touched.firstName && !firstName
        ? t(StringKeys.PLEASE_ENTER_YOUR_FIRST_NAME)
        : '',
    lastName:
      touched.lastName && !lastName
        ? t(StringKeys.PLEASE_ENTER_YOUR_LAST_NAME)
        : '',
    orgCode:
      touched.orgCode && !orgCode
        ? t(StringKeys.PLEASE_ENTER_A_VALID_ORGANIZATION_CODE)
        : '',
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handleEnterPin = async (pin: string) => {
    try {
      await addUserLocal(pin);
    } catch (e) {
      console.error('Error in handleEnterPin', e);
      showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
    }
  };

  const addUserLocal = useCallback(
    async (selectedPin: string) => {
      try {
        setLoadingAPIData(true);
        const response = await addUser(
          orgCode,
          DEFAULT_GROUP_NAME,
          firstName,
          lastName,
          phoneNumber,
          selectedPin,
        );

        // Check if the response indicates pending approval
        if (response.status === 'pending_approval' && response.join_request) {
          setPendingJoinRequest(response.join_request);
          return;
        }

        // User was created successfully
        if (response.status === 'user_created' && response.user_data) {
          const userData = response.user_data;

          try {
            const userConfig = await getConfigurationByOrgCodeAndId(
              orgCode,
              userData.org_spec_azure_id,
            );
            const updatedProfile: MergedUserSelfUpdateSettings = {
              arc_flash_detection_on: userData.arc_flash_detection_on,
              fall_detection_on: userData.fall_detection_on,
              head_impact_detection_on: userData.head_impact_detection_on,
              mute_sos_button: userData.mute_sos_button,
              already_accepted_spec_etag: userConfig._etag,
              org_user_azure_id: userData.org_user_azure_id,
              user_private_settings_azure_id:
                userData.user_private_settings_azure_id,
              language_code: 'en',
              // TODO: We probably want to change this at some point to let the user choose when signing up
            };
            try {
              await updateProfileSettings(updatedProfile);
            } catch (e) {
              console.error('Error updating profile settings', e);
              showErrorToast(
                t(
                  StringKeys.PROFILE_CREATED_BUT_SOME_SETTINGS_FAILED_TO_UPDATE,
                ),
              );
            }
          } catch (e) {
            console.error('Error getting config', e);
          }
          doIExist();
        }
      } catch (e) {
        console.error('Error adding user', e);
        showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
      } finally {
        setLoadingAPIData(false);
      }
    },
    [orgCode, firstName, lastName, phoneNumber, doIExist, showErrorToast, t],
  );

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handlePendingApprovalOk = () => {
    setPendingJoinRequest(null);
    doIExist();
  };

  const handleSignUp = async () => {
    setTouched({
      firstName: true,
      lastName: true,
      orgCode: true,
    });

    if (!firstName || !lastName || !orgCode) {
      showErrorToast(t(StringKeys.PLEASE_FILL_ALL_REQUIRED_FIELDS));
      return;
    }

    setIsSubmitting(true);
    setLoadingAPIData(true);
    try {
      const token = await getAccessTokenSilently();
      setBearerToken(token);
      const requiresPin = await getIfRequiresPin(orgCode);

      if (requiresPin) {
        setShowPinModal(true);
      } else {
        handleEnterPin('');
      }
    } catch (error) {
      console.error('Error handling sign up', error);
      showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
    } finally {
      setIsSubmitting(false);
      setLoadingAPIData(false);
    }
  };

  useEffect(() => {
    if (user) {
      setPhoneNumber(user.name ?? '');
    }
  }, [user]);

  useEffect(() => {
    const fetchToken = async () => {
      if (phoneNumber) {
        const token = await getAccessTokenSilently();
        setBearerToken(token);
      }
    };
    fetchToken();
  }, [phoneNumber, getAccessTokenSilently]);

  useEffect(() => {
    setLoading(false);
    setPageLoading(false);
  }, [bearerToken, setPageLoading]);

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#f5f5f5',
            p: { xs: 2, sm: 4 },
          }}
        >
          <Card
            elevation={3}
            sx={{
              width: '100%',
              maxWidth: 'sm',
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  textAlign: 'center',
                  fontWeight: 600,
                  mb: 4,
                }}
              >
                {t(StringKeys.SIGN_UP)}
              </Typography>

              <Box component="form" noValidate>
                <Stack spacing={3}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      label={t(StringKeys.FIRST_NAME)}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      onBlur={() => handleBlur('firstName')}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      variant="outlined"
                      disabled={isSubmitting}
                      required
                    />
                    <TextField
                      fullWidth
                      label={t(StringKeys.LAST_NAME)}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      onBlur={() => handleBlur('lastName')}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      variant="outlined"
                      disabled={isSubmitting}
                      required
                    />
                  </Stack>

                  <TextField
                    fullWidth
                    label={t(StringKeys.ORGANIZATION_CODE)}
                    value={orgCode}
                    onChange={(e) => setOrgCode(e.target.value.toUpperCase())}
                    onBlur={() => handleBlur('orgCode')}
                    error={!!errors.orgCode}
                    helperText={errors.orgCode}
                    variant="outlined"
                    disabled={isSubmitting}
                    required
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    disabled={isSubmitting}
                    onClick={handleSignUp}
                    sx={{
                      backgroundColor: COLORS.SAFEGUARD_ORANGE,
                      color: COLORS.WHITE,
                      py: 1.5,
                      '&:disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress
                        size={24}
                        sx={{ color: COLORS.WHITE }}
                      />
                    ) : (
                      t(StringKeys.SUBMIT)
                    )}
                  </Button>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                    sx={{ mt: 2 }}
                  >
                    <Trans
                      i18nKey={StringKeys.AGREE_TO_TERMS_FORMAT}
                      components={{
                        policy: (
                          <Link
                            href="https://www.safeguardequipment.com/privacy-policy-apps/"
                            color="primary"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                        eula: (
                          <Link
                            href="https://www.safeguardequipment.com/end-user-license-agreement/"
                            color="primary"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                      values={{
                        privacy_policy_text: t(StringKeys.PRIVACY_POLICY),
                        eula_text: t(StringKeys.END_USER_LICENSE_AGREEMENT),
                      }}
                    />
                    {'.'}
                  </Typography>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
      <EnterPinModal
        show={showPinModal}
        onHide={() => setShowPinModal(false)}
        handleEnterPin={handleEnterPin}
      />
      <LoadingData show={loadingAPIData} />

      {/* Pending Approval Modal */}
      <BaseModal
        open={!!pendingJoinRequest}
        onClose={handlePendingApprovalOk}
        title={t(StringKeys.AWAITING_ADMINISTRATOR_APPROVAL)}
        submitButtonText={t(StringKeys.OK)}
        onSubmit={handlePendingApprovalOk}
        size="sm"
        showCloseButton={false}
        footerContent={undefined}
      >
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: 'center', mt: 1 }}
        >
          {pendingJoinRequest &&
            t(StringKeys.YOU_REQUESTED_TO_JOIN_ORGANIZATION, {
              companyName: pendingJoinRequest.company_name,
              requestedDate: formatDate(pendingJoinRequest.requested_timestamp),
            })}
        </Typography>
      </BaseModal>
    </>
  );
};

const SignUpWithAuth = withAuthenticationRequired(SignUp, {
  onRedirecting: () => <Loading loadingText="Loading..." />,
});

export default SignUpWithAuth;
